import * as THREE from 'three';
import { createDeskStation } from './homepage-furniture';
import { animateCharacterIdle, animateCharacterTyping, disposeCharacter } from './homepage-character';

/**
 * Homepage diorama scene — isometric view of Hatchlings at desks.
 * OrthographicCamera for clean isometric aesthetic.
 * 4 desk stations, deliverable bubbles, warm lighting.
 */

// ── Scene state ──────────────────────────────────────────────
let scene: THREE.Scene;
let camera: THREE.OrthographicCamera;
let characters: THREE.Group[] = [];
let deskStations: THREE.Group[] = [];
let deliverables: THREE.Group[] = [];

const FRUSTUM_SIZE = 6;

// ── Colors ───────────────────────────────────────────────────
const BG_COLOR = 0xfff8f0;         // Warm cream
const PLATFORM_COLOR = 0xd4a574;   // Warm wood
const PLATFORM_EDGE = 0xc49464;    // Darker wood edge

// Deliverable bubble colors
const DELIVERABLE_COLORS = [
  0x6366f1, // code — indigo
  0xa855f7, // design — purple
  0x22c55e, // doc — green
  0xf59e0b, // chart — amber
];

const DELIVERABLE_LABELS = ['Code', 'Design', 'Doc', 'Plan'];

// ── Desk station positions on the platform ───────────────────
// Arranged in a 2x2 grid on the diorama
const STATION_POSITIONS: { pos: THREE.Vector3; rot: number }[] = [
  { pos: new THREE.Vector3(-1.0, 0.15, -0.4), rot: 0 },
  { pos: new THREE.Vector3(1.2, 0.15, -0.4), rot: 0 },
  { pos: new THREE.Vector3(-1.0, 0.15, 1.6), rot: 0 },
  { pos: new THREE.Vector3(1.2, 0.15, 1.6), rot: 0 },
];

/**
 * Create the homepage scene with diorama, desk stations, and deliverable bubbles.
 */
export function createHomepageScene(): void {
  scene = new THREE.Scene();
  scene.background = new THREE.Color(BG_COLOR);

  // ── Camera (isometric) ─────────────────────────────────────
  const aspect = window.innerWidth / window.innerHeight;
  camera = new THREE.OrthographicCamera(
    FRUSTUM_SIZE * aspect / -2,
    FRUSTUM_SIZE * aspect / 2,
    FRUSTUM_SIZE / 2,
    FRUSTUM_SIZE / -2,
    0.1,
    100,
  );
  camera.position.set(8, 8, 8);
  camera.lookAt(0, 0.5, 0.5);

  // ── Lighting ───────────────────────────────────────────────
  // Warm ambient
  scene.add(new THREE.AmbientLight(0xfff5e6, 0.5));

  // Main directional (warm sun from upper-left)
  const sun = new THREE.DirectionalLight(0xffeedd, 0.9);
  sun.position.set(5, 8, 3);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1024, 1024);
  sun.shadow.camera.near = 0.5;
  sun.shadow.camera.far = 30;
  sun.shadow.camera.left = -6;
  sun.shadow.camera.right = 6;
  sun.shadow.camera.top = 6;
  sun.shadow.camera.bottom = -6;
  scene.add(sun);

  // Subtle fill from opposite side
  const fill = new THREE.DirectionalLight(0xe8e0ff, 0.3);
  fill.position.set(-3, 4, -2);
  scene.add(fill);

  // Hemisphere for natural ground bounce
  const hemi = new THREE.HemisphereLight(0xfff8f0, 0xd4a574, 0.3);
  scene.add(hemi);

  // ── Diorama platform ───────────────────────────────────────
  const platformGroup = new THREE.Group();

  // Main surface
  const platformGeo = new THREE.BoxGeometry(5.5, 0.3, 4.5);
  const platformMat = new THREE.MeshStandardMaterial({
    color: PLATFORM_COLOR,
    roughness: 0.7,
  });
  const platform = new THREE.Mesh(platformGeo, platformMat);
  platform.position.y = 0;
  platform.receiveShadow = true;
  platform.castShadow = true;
  platformGroup.add(platform);

  // Darker edge band (slightly larger, slightly below)
  const edgeGeo = new THREE.BoxGeometry(5.7, 0.1, 4.7);
  const edgeMat = new THREE.MeshStandardMaterial({
    color: PLATFORM_EDGE,
    roughness: 0.8,
  });
  const edge = new THREE.Mesh(edgeGeo, edgeMat);
  edge.position.y = -0.2;
  platformGroup.add(edge);

  scene.add(platformGroup);

  // ── Desk stations ──────────────────────────────────────────
  characters = [];
  deskStations = [];

  STATION_POSITIONS.forEach((station, i) => {
    const { group, character } = createDeskStation(station.pos, station.rot, i);
    deskStations.push(group);
    characters.push(character);
    scene.add(group);

    // Initially hide all except first two stations
    if (i > 1) {
      group.visible = false;
      group.scale.setScalar(0);
    }
  });

  // ── Deliverable bubbles ────────────────────────────────────
  deliverables = [];

  characters.forEach((char, i) => {
    const bubble = createDeliverableBubble(
      DELIVERABLE_COLORS[i % DELIVERABLE_COLORS.length],
      DELIVERABLE_LABELS[i % DELIVERABLE_LABELS.length],
    );
    // Position above the character's head (relative to station)
    const stationPos = STATION_POSITIONS[i].pos;
    bubble.position.set(stationPos.x, 2.0, stationPos.z + 0.55);
    bubble.visible = false;
    bubble.scale.setScalar(0);
    deliverables.push(bubble);
    scene.add(bubble);
  });
}

/**
 * Create a deliverable bubble — a small floating card above a worker.
 */
function createDeliverableBubble(color: number, _label: string): THREE.Group {
  const group = new THREE.Group();

  // Card body
  const cardGeo = new THREE.BoxGeometry(0.5, 0.35, 0.05);
  const cardMat = new THREE.MeshStandardMaterial({
    color,
    transparent: true,
    opacity: 0.9,
    roughness: 0.3,
    emissive: color,
    emissiveIntensity: 0.15,
  });
  const card = new THREE.Mesh(cardGeo, cardMat);
  group.add(card);

  // White inner panel (like a document)
  const innerGeo = new THREE.BoxGeometry(0.42, 0.27, 0.02);
  const innerMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.85,
    roughness: 0.5,
  });
  const inner = new THREE.Mesh(innerGeo, innerMat);
  inner.position.z = 0.03;
  group.add(inner);

  // Small "content lines" represented by thin boxes
  for (let i = 0; i < 3; i++) {
    const lineWidth = 0.3 - i * 0.06;
    const lineGeo = new THREE.BoxGeometry(lineWidth, 0.02, 0.01);
    const lineMat = new THREE.MeshStandardMaterial({
      color,
      transparent: true,
      opacity: 0.4,
    });
    const line = new THREE.Mesh(lineGeo, lineMat);
    line.position.set(-0.03, 0.06 - i * 0.07, 0.04);
    group.add(line);
  }

  return group;
}

/**
 * Update homepage scene — animate characters each frame.
 */
export function updateHomepageScene(elapsed: number): void {
  characters.forEach((char, i) => {
    if (!char.parent?.visible) return;

    // First two characters type (visible from start), others idle
    if (i <= 1) {
      animateCharacterTyping(char, elapsed);
    } else {
      animateCharacterIdle(char, elapsed);
    }
  });

  // Deliverable bubbles gentle float
  deliverables.forEach((bubble, i) => {
    if (!bubble.visible) return;
    bubble.position.y = 2.0 + Math.sin(elapsed * 1.5 + i * 1.2) * 0.06;
    bubble.rotation.y = Math.sin(elapsed * 0.5 + i * 0.8) * 0.1;
  });
}

/**
 * Get the homepage camera.
 */
export function getHomepageCamera(): THREE.OrthographicCamera {
  return camera;
}

/**
 * Get the homepage scene.
 */
export function getHomepageScene(): THREE.Scene {
  return scene;
}

/**
 * Get character groups for scroll animation control.
 */
export function getHomepageCharacters(): THREE.Group[] {
  return characters;
}

/**
 * Get desk station groups for scroll animation control.
 */
export function getHomepageDeskStations(): THREE.Group[] {
  return deskStations;
}

/**
 * Get deliverable bubbles for scroll animation control.
 */
export function getHomepageDeliverables(): THREE.Group[] {
  return deliverables;
}

/**
 * Handle window resize — update OrthographicCamera frustum.
 */
export function resizeHomepage(): void {
  if (!camera) return;
  const aspect = window.innerWidth / window.innerHeight;
  camera.left = FRUSTUM_SIZE * aspect / -2;
  camera.right = FRUSTUM_SIZE * aspect / 2;
  camera.top = FRUSTUM_SIZE / 2;
  camera.bottom = FRUSTUM_SIZE / -2;
  camera.updateProjectionMatrix();
}

/**
 * Dispose all homepage scene resources.
 */
export function disposeHomepageScene(): void {
  characters.forEach(disposeCharacter);
  characters = [];
  deskStations = [];
  deliverables = [];

  if (scene) {
    scene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.geometry?.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
  }
}
